import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, TextField, Typography, Modal, Divider, ButtonGroup, Tooltip, Paper } from '@mui/material';
import { Edit as EditIcon, Print as PrintIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useUpdatePropertyMutation, useDeletePropertyMutation } from '../store/docApi';
import ItemMetadata from '../components/widgets/ItemMetadata';
import { formatDateTime } from '../utils';
import TiptapEditor, { markdownToHtml } from './TipTapEditor';
import TextViewer from './TextViewer';
import { getReferencesHtml, NoteReferences } from './NoteReferences';

export default function NoteDetails({ item, noteid, connectionIndex }) {
  const [updateProperty] = useUpdatePropertyMutation();
  const [deleteProperty] = useDeletePropertyMutation();
  const navigate = useNavigate();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [note, setNote] = useState({});
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [text, setText] = useState('');
  const [startDate, setStartDate] = useState('');

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const pad = (n) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  useEffect(() => {
    if (item) {
      const _note = item.connections[connectionIndex].notes.find((n) => n.id === noteid);
      if (_note) {
        setNote(_note);
        setName(_note.name || '');
        setDescription(_note.description || '');
        setText(_note.text || '');
        setStartDate(formatDateForInput(_note.startDate));
      }
    }
  }, [item, noteid, connectionIndex]);

  function updateReferences({ newrefs, path }) {
    updateProperty({ 
      label: item.label, 
      pk: item.pk, 
      id: item.id, 
      name: path,
      value: newrefs 
    });
  }

  async function deleteNote() {
    const noteIndex = item.connections[connectionIndex].notes.findIndex((n) => n.id === noteid);
    if (noteIndex === -1) return;

    await deleteProperty({
      label: item.label,
      pk: item.pk,
      id: item.id,
      name: `connections/${connectionIndex}/notes/${noteIndex}`
    });

    setDeleteConfirmOpen(false);
    navigate(`/projects/${item.pk}/${item.id}/${connectionIndex}/notes`);
  }

  const saveNote = () => {
    setIsEditing(false);
    const noteIndex = item.connections[connectionIndex].notes.findIndex((n) => n.id === noteid);
    if (noteIndex === -1) return;

    if (name !== note.name) {
      updateProperty({ 
        label: item.label, 
        pk: item.pk, 
        id: item.id, 
        name: `connections/${connectionIndex}/notes/${noteIndex}/name`, 
        value: name 
      });
    }
    if (description !== note.description) {
      updateProperty({ 
        label: item.label, 
        pk: item.pk, 
        id: item.id, 
        name: `connections/${connectionIndex}/notes/${noteIndex}/description`, 
        value: description 
      });
    }
    if (text !== note.text) {
      updateProperty({ 
        label: item.label, 
        pk: item.pk, 
        id: item.id, 
        name: `connections/${connectionIndex}/notes/${noteIndex}/text`, 
        value: text 
      });
    }
    if (startDate !== formatDateForInput(note.startDate)) {
      updateProperty({ 
        label: item.label, 
        pk: item.pk, 
        id: item.id, 
        name: `connections/${connectionIndex}/notes/${noteIndex}/startDate`, 
        value: new Date(startDate).toISOString()
      });
    }
  };

  const handleChange = (setter) => (e) => setter(e.target.value);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const htmltext = markdownToHtml(note.text);
    printWindow.document.write(`
        <html><head><title>${note.name}</title>
        <style>
          @page { margin: 0 }
          body { font-family: "Times New Roman", Times, serif; font-size: 12pt; margin: 1in; }
          h3 { margin-bottom: 10px; }
          p { font-size: 12pt; }
        </style>  
        </head>
        <body>
            <p><strong>${note.name || ''}</strong></p>
            <p>${note.description || ''}</p>
            <p>${htmltext}</p>
            ${note.references && note.references.length > 0 ? '<b>References</b>' : ''}
            <p>${getReferencesHtml(note.references, item.bibliography)}</p>
        </body>
        </html>`);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    item &&
    note && (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4">{note.name}</Typography>
            <Typography variant="h6">{note.description}</Typography>
          </Box>
          {isEditing ? (
            <ButtonGroup variant="outlined" size="small">
              <Button onClick={saveNote} variant="contained" color="primary" startIcon={<EditIcon />}>
                Save
              </Button>
              <Button onClick={() => setIsEditing(false)} variant="outlined" color="secondary">
                Cancel
              </Button>
            </ButtonGroup>
          ) : (
            <ButtonGroup variant="outlined" size="small">
              <Tooltip title="Edit Note">
                <Button startIcon={<EditIcon />} onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              </Tooltip>
              <Tooltip title="Print Note">
                <Button startIcon={<PrintIcon />} onClick={handlePrint}>
                  Print
                </Button>
              </Tooltip>
              <Tooltip title="Delete Note">
                <Button startIcon={<DeleteIcon />} color="error" onClick={() => setDeleteConfirmOpen(true)}>
                  Delete
                </Button>
              </Tooltip>
            </ButtonGroup>
          )}
        </Box>
        {isEditing ? (
          <Box>
            <Typography>Name:</Typography>
            <TextField value={name} onChange={handleChange(setName)} fullWidth size="small" />
            <Typography>Description:</Typography>
            <TextField value={description} onChange={handleChange(setDescription)} fullWidth size="small" />
            <Typography>Date:</Typography>
            <TextField 
              type="datetime-local" 
              value={startDate} 
              onChange={handleChange(setStartDate)} 
              fullWidth 
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Typography>Note:</Typography>
            <TiptapEditor content={text} setContent={setText} />
          </Box>
        ) : (
          <Box>
            <Typography>{formatDateTime(note.startDate)}</Typography>
            <Divider sx={{ my: 2 }} />
            <TextViewer content={note.text} />
            <Divider sx={{ my: 2 }} />
          </Box>
        )}
        <NoteReferences 
          references={note.references} 
          bibliography={item.bibliography} 
          updateReferences={updateReferences}
          connectionIndex={connectionIndex}
          noteIndex={item.connections[connectionIndex].notes.findIndex((n) => n.id === noteid)}
        />
        <ItemMetadata item={item} subitem={note} />
        <Modal 
          open={deleteConfirmOpen} 
          onClose={() => setDeleteConfirmOpen(false)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Paper sx={{ p: 3, maxWidth: 400 }}>
            <Typography variant="h6" gutterBottom>Delete Confirmation</Typography>
            <Typography variant="body1" gutterBottom>Are you sure you want to delete this note?</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined">
                Cancel
              </Button>
              <Button onClick={deleteNote} variant="contained" color="error">
                Delete
              </Button>
            </Box>
          </Paper>
        </Modal>
      </Box>
    )
  );
}
